import { createFileRoute, Link } from "@tanstack/react-router";
import {
	Bot,
	CalendarClock,
	GitBranch,
	MessageSquare,
	Sparkles,
	Workflow,
} from "lucide-react";
import {
	type ComparisonRow,
	ComparisonTable,
} from "@/components/comparison-table";
import { FeatureGrid } from "@/components/feature-grid";
import { MarketingHero } from "@/components/marketing-hero";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { MarketingSection } from "@/components/marketing-section";
import { PromptCalendarExample } from "@/components/prompt-calendar-example";
import { buildPageHead } from "@/lib/seo-meta";

const path = "/alternatives/buffer";

export const Route = createFileRoute(path)({
	component: BufferAlternative,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: path,
			title: "AgentReacher | Buffer Alternative for AI Social Media Scheduling",
			description:
				"AgentReacher is a Buffer alternative for AI social media agents. Use Claude, ChatGPT, or any MCP assistant to draft, adapt, schedule, and publish from chat.",
		}),
});

const comparison: ComparisonRow[] = [
	{ feature: "AI agent workflow", competitor: false, agentreacher: true },
	{ feature: "MCP integration", competitor: false, agentreacher: true },
	{ feature: "Bluesky support", competitor: false, agentreacher: true },
	{ feature: "Threads support", competitor: "Limited", agentreacher: true },
	{ feature: "Cross-posting", competitor: true, agentreacher: true },
	{
		feature: "Per-platform variations",
		competitor: "Manual",
		agentreacher: "Auto",
	},
	{
		feature: "Approval flow",
		competitor: "Paid add-on",
		agentreacher: "Built in",
	},
	{
		feature: "Starting plan",
		competitor: "From €6/channel",
		agentreacher: "From €19/mo",
	},
];

function BufferAlternative() {
	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="Buffer alternative"
				title="A Buffer alternative for AI social media agents."
				description="Buffer helps you maintain queues. AgentReacher lets your AI assistant build the queue for you: draft the posts, rewrite per network, schedule the week, and ask for approval."
			/>

			<MarketingSection
				title="Why teams switch from Buffer"
				description="Buffer was built for manual queues. AgentReacher is built for teams already creating content inside Claude, ChatGPT, Cursor, or another MCP host."
			>
				<FeatureGrid
					features={[
						{
							icon: Bot,
							title: "AI-first drafting",
							description:
								"Your agent learns your voice and drafts posts that read like you, not like an LLM.",
						},
						{
							icon: Workflow,
							title: "MCP-native",
							description:
								"Drive scheduling and publishing from Claude, Cursor, or ChatGPT without leaving the chat.",
						},
						{
							icon: GitBranch,
							title: "Modern platforms",
							description:
								"First-class Bluesky and Threads support, alongside the platforms Buffer covers.",
						},
						{
							icon: CalendarClock,
							title: "One calendar, every channel",
							description:
								"Drag, edit, and regenerate posts from a unified view  -  no per-channel queue maintenance.",
						},
						{
							icon: Sparkles,
							title: "Per-platform automatic variations",
							description:
								"The agent rewrites for each network so you do not maintain six versions of every post.",
						},
						{
							icon: MessageSquare,
							title: "Built-in approvals",
							description:
								"Reviewer roles and approval gates ship with every plan, not as a paid extra.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection title="AgentReacher vs. Buffer">
				<ComparisonTable competitorName="Buffer" rows={comparison} />
			</MarketingSection>

			<MarketingSection
				title="Live prompt-to-calendar example"
				description="The faster workflow is not copying AI drafts into a queue. It is asking your assistant to build the queue directly."
			>
				<PromptCalendarExample
					assistant="AI social media agent"
					prompt="Take this blog post and turn it into four scheduled updates: LinkedIn summary, X thread, Bluesky take, and a follow-up question for Friday."
					response="I created the platform-specific drafts, preserved the same core point, and scheduled each one to avoid posting everything at once."
					slots={[
						{
							day: "Monday",
							time: "09:15",
							channel: "LinkedIn",
							title:
								"Executive summary with a practical takeaway for marketers.",
						},
						{
							day: "Tuesday",
							time: "12:00",
							channel: "X",
							title:
								"Six-post thread turning the blog into a skimmable argument.",
						},
						{
							day: "Friday",
							time: "10:20",
							channel: "Bluesky",
							title:
								"Follow-up question that points back to the original idea.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection title="Migrating in an afternoon">
				<div className="mx-auto max-w-2xl space-y-4 text-sm leading-relaxed text-muted-foreground">
					<p>
						Most teams switch in a single afternoon. Connect the same accounts
						you use in Buffer, point the agent at a few past posts so it learns
						your voice, and ask it to rebuild the next two weeks of content. Run
						both tools in parallel until the calendar looks right, then move the
						publishing habit into AgentReacher.
					</p>
					<p>
						For the deeper comparison, see our{" "}
						<Link
							to="/blog"
							className="text-primary transition-colors hover:text-primary/80"
						>
							latest blog guides
						</Link>{" "}
						or pick a channel from the site navigation and review the
						platform-specific scheduler workflow.
					</p>
				</div>
			</MarketingSection>
		</MarketingPageShell>
	);
}
